/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import EmberObject, { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import {
  byKeys,
  filterBy,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';

export default class AssessmentGradesOverview extends Component {
  AssessmentDisplay = AssessmentDisplay;

  // Services
  @service
  dates;

  @service
  session;

  @service
  store;

  @service
  terms;

  @tracked current = false;
  @tracked reportDate = null;

  @computed('terms.currentTerm', 'reportDate')
  get currentTerm() {
    if (isEmpty(this.reportDate)) {
      return this.terms.currentTerm;
    }
    return this.terms.currentTermAtDate(this.reportDate);
  }

  @tracked timeframes = [];
  @tracked maxGradeColumns = 12;
  @tracked isParentStudentView = false;

  @computed('gradeColumnsToShow', 'current')
  get splitTermHeaders() {
    return !this.current && this.gradeColumnsToShow >= 3;
  }

  init() {
    super.init(...arguments);
    let schoolId = this.get('session.activeSchool');
    this.store.query('timeframe', { schoolId }).then((timeframes) => {
      this.timeframes = timeframes
        .slice()
        .sort((a, b) => a.startDateUnix - b.startDateUnix);
    });
  }

  @computed(
    'isParentStudentView',
    'assessment.{noGradesColumnsToShowCaregiversStudents,noGradesColumnsToShowStaff}',
  )
  get gradeColumnsToShow() {
    if (this.isParentStudentView) {
      if (this.assessment.get('noGradesColumnsToShowCaregiversStudents')) {
        return this.assessment.get('noGradesColumnsToShowCaregiversStudents');
      } else {
        return 4;
      }
    } else {
      if (this.assessment.get('noGradesColumnsToShowStaff')) {
        return this.assessment.get('noGradesColumnsToShowStaff');
      } else {
        return 8;
      }
    }
  }

  @computed('timeframes.@each.isCurrent')
  get currentTimeframe() {
    let timeframes = this.timeframes;
    if (isEmpty(timeframes)) {
      return null;
    }
    let currentTimeframe = timeframes.find((timeframe) => timeframe.isCurrent);
    if (isEmpty(currentTimeframe)) {
      // We're in between timeframes
      return null;
    }
    return currentTimeframe;
  }

  @computed('gradeColumnsToShow', 'timeframes', 'currentTimeframe')
  get showTimeframes() {
    let index = 0;
    let minIndex = 0;
    let closestTimeframe = this.dates.findClosestTimeframe(this.timeframes);
    if (!isEmpty(this.currentTimeframe)) {
      index = this.timeframes.indexOf(this.currentTimeframe);
      minIndex = Math.max(0, index - this.gradeColumnsToShow + 1);
    } else {
      // we're in between timeframes, return the closest one and beyond
      index = this.timeframes.indexOf(closestTimeframe);
      minIndex = Math.max(0, index - this.gradeColumnsToShow + 1);
    }
    return this.timeframes.slice(minIndex, index + 1).reverse();
  }

  @computed('timeframes.[]', 'assessment.assessmentDisplay')
  get timeframesDisplay() {
    return (
      this.get('assessment.assessmentDisplay') ===
        AssessmentDisplay.ALTERNATE && this.timeframes.length >= 1
    );
  }

  @computed('gradeColumnsToShow')
  get showMonths() {
    const months = [];
    const today = moment();

    for (let i = 0; i < this.gradeColumnsToShow; i++) {
      const previousMonth = moment(today)
        .subtract(i, 'months')
        .startOf('month');
      months.push(previousMonth.format('MMM, YYYY'));
    }

    return months;
  }

  @computed('current', 'terms.terms.[]', 'currentTerm', 'gradeColumnsToShow')
  get showTerms() {
    if (this.current) {
      // Only show the current term - don't find the previous X terms.
      if (isEmpty(this.currentTerm)) {
        // Current term not loaded yet?
        return [];
      } else {
        return [this.currentTerm];
      }
    }
    let sorted = this.terms.terms
      .slice()
      .sort((a, b) => (a.start || '').localeCompare(b.start || ''));
    if (!isEmpty(this.currentTerm)) {
      let index = sorted.indexOf(this.currentTerm);
      let minIndex = Math.max(0, index - this.gradeColumnsToShow + 1);
      return sorted.slice(minIndex, index + 1).reverse();
    }
    return sorted.reverse();
  }

  @computed('currentTerm', 'terms.terms.[]', 'maxGradeColumns')
  get termsData() {
    let sorted = this.terms.terms
      .slice()
      .sort((a, b) => (a.start || '').localeCompare(b.start || ''));
    if (!isEmpty(this.currentTerm)) {
      let index = sorted.indexOf(this.currentTerm);
      let minIndex = Math.max(0, index - this.maxGradeColumns + 1);
      return sorted.slice(minIndex, index + 1).reverse();
    }
    return sorted.reverse();
  }

  @computed(
    'termsData.@each.start',
    'grades.@each.createTime',
    'assessment.assessmentDisplay',
    'showMonths',
    'showTimeframes.@each.{startDateUnix,endDateUnix,columnLabel}',
  )
  get data() {
    let terms = this.termsData;
    let months = this.showMonths;
    let timeframes = this.showTimeframes;
    let modules = this.assessment.sortedModules;
    let grades = this.grades;
    let data = [];
    let gradesForMonth = EmberObject.create({});

    switch (this.get('assessment.assessmentDisplay')) {
      case AssessmentDisplay.MONTHS:
        grades.forEach(function (grade) {
          let gradeMonth = moment.unix(grade.createTime).format('MMM, YYYY');
          if (!gradesForMonth[gradeMonth]) {
            gradesForMonth[gradeMonth] = [grade];
          } else {
            gradesForMonth[gradeMonth].push(grade);
          }
        });
        months.forEach(function (month) {
          modules.forEach(function (module) {
            if (!isEmpty(gradesForMonth[month])) {
              let grade = filterBy(
                gradesForMonth[month],
                'module.id',
                module.id,
              )
                .sort(byKeys('createTime'))
                .at(-1);

              if (grade) {
                data.push({
                  month: moment.unix(grade.createTime).format('MMM, YYYY'),
                  moduleId: module.id,
                  grade,
                });
              }
            }
          });
        });
        break;
      case AssessmentDisplay.ALTERNATE:
        timeframes.forEach(function (timeframe) {
          modules.forEach(function (module) {
            let grade = grades
              .filter((g) => {
                return (
                  g.module_id === module.id &&
                  g.createTime >= timeframe.startDateUnix &&
                  g.createTime < timeframe.endDateUnix
                );
              })
              .sort((a, b) =>
                a.createTime > b.createTime
                  ? 1
                  : a.createTime < b.createTime
                    ? -1
                    : 0,
              )
              .at(-1);

            if (grade) {
              data.push({
                columnLabel: timeframe.columnLabel + timeframe.year,
                moduleId: module.id,
                grade,
              });
            }
          });
        });
        break;
      default:
        terms.forEach(function (term) {
          modules.forEach(function (module) {
            let grade = grades
              .filter((g) => {
                if (g.module_id !== module.id) {
                  return false;
                }

                let next = term.get('next.start');
                let gradeDate = g.get('date');
                if (next) {
                  return term.get('start') <= gradeDate && gradeDate < next;
                }
                return term.get('start') <= gradeDate;
              })
              .sort((a, b) =>
                a.createTime > b.createTime
                  ? 1
                  : a.createTime < b.createTime
                    ? -1
                    : 0,
              )
              .at(-1);

            if (grade) {
              data.push({
                termId: term.id,
                moduleId: module.id,
                grade,
              });
            }
          });
        });
    }
    return data;
  }

  @computed('showTerms.[]', 'data.@each.termId')
  get termsWithData() {
    let data = this.data;
    return this.showTerms.filter((term) => {
      return findBy(data, 'termId', term.id);
    });
  }
}
